import { Button, Divider, Drawer, Message, Space } from "@bedrock/components";
import React, { useEffect } from "react";
import style from './index.less';
import classNames from "classnames/bind";
import withFunctionCall from "@mpc/common/src/utils/withFunctionCall";
import { demandCoopKolInfoGetKolInfoCoop, demandSourceGetContentLoginUrl, demandSourceGetKolInfoUsingPost } from "#services/api";
import { ChevronDown, Plus } from "@bedrock/icons-react";
import { useMemoizedFn } from "ahooks";
import AddExpertModal from "../KOLInquiryForQuotation/AddExpertModal";

const cx = classNames.bind(style);

interface StarSelectDrawerProps {
  visible?: boolean;
  onCancel?: () => void;
  title?: string;
  l3_category_id: number;
  manualAddable?: boolean; // 是否支持手动添加达人
  onOk?: (data: any) => void;
  dataType?: 'kol' | 'cooperate'; // 数据类型，kol: 一询达人，cooperate: 合作内容
}

function StarSelectDrawer(props: StarSelectDrawerProps) {
  const { visible, onCancel, title = '添加已收录达人', l3_category_id, manualAddable = true, dataType = 'kol' } = props;
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const [submiting, setSubmiting] = React.useState(false);
  const [contentUrl, setContentUrl] = React.useState<string>('');
  const [data, setData] = React.useState<any>({});
  const [planData, setPlanData] = React.useState<any>([]);

  const handleCancel = () => {
    onCancel?.()
  }

  const handleSubmit = async () => {
    if (!data.ready) {
      Message.info('请等待页面加载完成，然后再进行操作，若长时间未加载完成，请刷新页面或联系管理员');
      return;
    }
    if (!data?.selectedRows?.length && !planData?.length) {
      Message.info('请先选择达人');
      return;
    }
    if (data?.selectedRows?.length) {
      setSubmiting(true);
      switch (dataType) {
        case 'kol':
          demandSourceGetKolInfoUsingPost({
            main_platform_link: data?.selectedRows?.map((item: any) => item.kolMainPage),
            content_type: l3_category_id,
          }).then((res) => {
            props?.onOk?.({
              contentData: res?.data?.map(v => ({
                ...v,
                import_type: 'FROM_CONTENT',
              })),
              planData,
            });
            onCancel?.();
          }).finally(() => {
            setSubmiting(false);
          })
          break;
        case 'cooperate':
          demandCoopKolInfoGetKolInfoCoop({
            main_platform_link: data?.selectedRows?.map((item: any) => item.kolMainPage),
            content_type: l3_category_id,
          }).then((res) => {
            props?.onOk?.({
              contentData: res?.data || [],
              planData,
            });
            onCancel?.();
          }).finally(() => {
            setSubmiting(false);
          })
          break;
        default:
          props?.onOk?.({
            contentData: [],
            planData,
          });
          onCancel?.();
          break;
      }
    } else {
      props?.onOk?.({
        contentData: [],
        planData,
      });
      onCancel?.();
    }
  }

  const handleViewSelectedChange = () => {
    if (!data?.ready) {
      Message.info('请等待页面加载完成，然后再进行操作');
      return;
    }
    if (!data?.viewSelected && !data?.selectedRows?.length) {
      Message.info('请先选择达人');
      return;
    }
    const v = !data?.viewSelected;
    iframeRef.current?.contentWindow?.postMessage({ type: 'SelectedItemModalVisibleChange', data: v }, '*');
    setData({ ...data, viewSelected: v });
  }

  const handleMessage = useMemoizedFn((event: any) => {
      const { type, data: messageData } = event.data || {};
      if (type === 'ready') {
        setData({
          ...data,
          ready: true,
        })
        return;
      }
      if (type === 'selectedRows') {
        setData({
          ...data,
          selectedRows: messageData,
        })
        return;
      }
        // if (event.origin !== tempUrl) return;
    })

  useEffect(() => {
    demandSourceGetContentLoginUrl({}).then((res) => {
      if (!res?.data) {
        Message.error('获取内容页面失败');
        return;
      }
      setContentUrl(res?.data);
      window.addEventListener('message', handleMessage);
    })

    return () => {
      window.removeEventListener('message', handleMessage);
    }
  }, [])

  const handleAddPlanData = () => {
    AddExpertModal.show({
      l3_category_id,
      planData,
      onOk: (newData) => {
        setPlanData(newData || []);
      }
    })
  }

  return (
    <Drawer
      title={title}
      visible={visible}
      // width={1200}
      placement="bottom"
      height={'100vh'}
      zIndex={1000}
      getContainer={false}
      // maskStyle={{ backgroundColor: 'transparent' }}
      maskClosable={false}
      bodyClassName={cx('drawer-body')}
      onClose={() => {
        handleCancel()
      }}
      destroyOnClose
      footer={
          <Space direction="horizontal" style={{ justifyContent: 'space-between'}}>
              {manualAddable ? <Button type='checked' icon={<Plus />} onClick={handleAddPlanData}>手动添加</Button> : <span></span>}
              <Space size={20}>
                  <div className="flex justify-start items-center gap-2 --text-color-2">
                    {manualAddable && <>
                    <div>已手动添加达人：
                      <Button 
                        type="link" 
                        className="fs-16 fw-bold" 
                        onClick={handleAddPlanData} 
                        suffixIcon={<ChevronDown className='text-[#525566]' />}
                      >
                        {planData.length}
                      </Button>
                    </div></>}
                    <Divider type="vertical" />
                    <div>已选择在库达人：
                      <Button 
                        onClick={handleViewSelectedChange} 
                        type="link" 
                        className="fs-16 fw-bold" 
                        suffixIcon={<ChevronDown className='text-[#525566]' />}
                      >
                        {data?.selectedRows?.length || 0}
                      </Button>
                    </div>
                  </div>
                  <Space size="small">
                    <Button onClick={handleCancel} loading={submiting}>取消</Button>
                    <Button type="primary" onClick={handleSubmit} loading={submiting}> 提交</Button>
                  </Space>
              </Space>
          </Space>
      }
    >
      {contentUrl ? (
        <iframe 
          id="targetFrame" 
          width="100%"
          height="99%"
          ref={iframeRef}
          src={contentUrl}
          // @ts-ignore
          frameborder="0"
          allow="clipboard-write; clipboard-read"
          crossorigin="anonymous"></iframe>
      )
      : <p>正在加载内容页面...</p>}
    </Drawer>
  )
}

export default withFunctionCall(StarSelectDrawer);